/*
 * Copyright 2021 LABOR.digital
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Last modified: 2021.04.08 at 22:46
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { emitDomEvent, forEach, getGuid } from '@labor-digital/helferlein';
var HmrRegistry = /** @class */ (function () {
    function HmrRegistry() {
    }
    /**
     * Executed every time a bit app is constructed to make it findable by the registry
     * @param app
     */
    HmrRegistry.registerApp = function (app) {
        HmrRegistry._apps.add(app);
    };
    /**
     * Used by the mount class to register its HTML element in this registry.
     * Only ctors that have a @Hot decorator applied to them will be considered, all other ctors are ignored.
     *
     * @param el
     * @param ctor
     */
    HmrRegistry.registerMount = function (el, ctor) {
        if (!ctor.__hmrId) {
            return;
        }
        var id = ctor.__hmrId;
        // Register the HTML node by the hmr module id
        var mountMap = HmrRegistry._mountsById;
        if (!mountMap.has(id)) {
            mountMap.set(id, new Set());
        }
        mountMap.get(id).add(el);
        // Register the hmr module id by the HTML node
        var ctorMap = HmrRegistry._idByMount;
        ctorMap.set(el, id);
    };
    /**
     * Used by the mount class to remove itself from this registry when the HTML node is disconnected from the DOM.
     * @param el
     */
    HmrRegistry.unregisterMount = function (el) {
        var ctorMap = HmrRegistry._idByMount;
        var id = ctorMap.get(el);
        if (!id) {
            return;
        }
        ctorMap.delete(el);
        var mountMap = HmrRegistry._mountsById;
        if (!mountMap.has(id)) {
            return;
        }
        var elList = mountMap.get(id);
        elList.delete(el);
        if (elList.size === 0) {
            mountMap.delete(id);
        }
    };
    /**
     * Registers a NEW bit constructor as a hot reloadable module
     * @param ctor
     */
    HmrRegistry.registerCtor = function (ctor) {
        ctor.__hmrId = getGuid('hmrBitCtor-');
        HmrRegistry._hmrBits.set(ctor.__hmrId, ctor);
    };
    /**
     * Replaces an existing bit constructor with a newly loaded module
     * @param id
     * @param newCtor
     */
    HmrRegistry.replaceCtor = function (id, newCtor) {
        if (!HmrRegistry._hmrBits.has(id)) {
            throw new Error('Error while applying hot reload patch! Could not find hmr ctor with id ' + id);
        }
        var oldCtor = HmrRegistry._hmrBits.get(id);
        newCtor.__hmrId = id;
        HmrRegistry._hmrBits.set(id, newCtor);
        HmrRegistry.patchApps(oldCtor, newCtor);
        HmrRegistry.patchMounts(id);
    };
    /**
     * Iterates all registered apps and replaces the old constructor with the new constructor in the bit registry.
     *
     * @param oldCtor
     * @param newCtor
     * @protected
     */
    HmrRegistry.patchApps = function (oldCtor, newCtor) {
        forEach(HmrRegistry._apps, function (app) {
            var bits = app.di.bitRegistry.getAll();
            forEach(bits, function (ctor, type) {
                if (ctor !== oldCtor) {
                    return;
                }
                bits.set(type, newCtor);
            });
        });
    };
    /**
     * Receives a list of css selectors, finds the matching dom nodes and forces the mount to create a new bit instance
     * @param id
     * @protected
     */
    HmrRegistry.patchMounts = function (id) {
        var _this = this;
        var mountMap = HmrRegistry._mountsById;
        if (!mountMap.has(id)) {
            return;
        }
        forEach(mountMap.get(id), function (el) { return __awaiter(_this, void 0, void 0, function () {
            var mount, isConnected;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        mount = el._bitMount;
                        isConnected = mount.isConnected;
                        mount.disconnect(true);
                        if (!isConnected) return [3 /*break*/, 2];
                        return [4 /*yield*/, mount.connect(el)];
                    case 1:
                        _a.sent();
                        // Make sure that all parent mounts get rebound
                        emitDomEvent(el, 'domChange');
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        }); });
    };
    /**
     * The list of all bit apps that are scanned when a bit constructor gets replaced
     * @protected
     */
    HmrRegistry._apps = new Set();
    /**
     * The list of all mount elements by their matching hmr module id
     * @protected
     */
    HmrRegistry._mountsById = new Map();
    /**
     * The list that maps a single mount element to its matching hmr module id
     * @protected
     */
    HmrRegistry._idByMount = new Map();
    /**
     * The list of all bit constructors that are decorated using @Hot(module)
     * @protected
     */
    HmrRegistry._hmrBits = new Map();
    return HmrRegistry;
}());
export { HmrRegistry };
//# sourceMappingURL=HmrRegistry.js.map